/**
 * Shared Tailwind classes for custom dropdown list rows (BaseDropdown, creatable selects, side panel).
 * Keeps selected / unselected / disabled states aligned across the design system.
 */

export type DropdownTheme = 'default' | 'sales' | 'dd'
export type SalesDropdownTriggerContext = 'embedded' | 'compact'
/** `teleport` = menu rendered on body; `inline` = menu anchored under trigger inside panel */
export type SalesDropdownMenuMount = 'teleport' | 'inline'

const optionRowBaseDefault =
  'w-full flex items-center justify-between gap-2.5 px-6 py-2.5 text-sm cursor-pointer transition-colors text-left'

const optionRowBaseSales =
  'w-full flex items-center gap-2 rounded px-2 py-1 text-xs cursor-pointer transition-colors text-left'

/** Single- or multi-select option row (one row, may be selected). */
export function dropdownOptionRowClass(
  selected: boolean,
  disabled: boolean,
  theme: DropdownTheme = 'default'
): string {
  if (theme === 'sales') {
    if (disabled) {
      return `${optionRowBaseSales} text-text-muted cursor-not-allowed opacity-60`
    }
    if (selected) {
      return `${optionRowBaseSales} sd-dropdown-option--selected font-medium`
    }
    return `${optionRowBaseSales} text-text-secondary hover:bg-surface-hover`
  }

  if (disabled) {
    return `${optionRowBaseDefault} text-text-muted cursor-not-allowed bg-surface-muted hover:bg-surface-muted`
  }
  if (selected) {
    return `${optionRowBaseDefault} text-primary bg-primary/10`
  }
  return `${optionRowBaseDefault} text-text-secondary hover:bg-surface-hover`
}

/** Default dropdown trigger (compact `py-1` — lines up with filter-row date fields). */
export const dropdownDefaultTriggerClass =
  'px-3 py-1 bg-surface-muted border border-border rounded-lg text-sm hover:not(:disabled):border-dropdown-focus focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-surface-muted disabled:text-text-muted disabled:cursor-not-allowed disabled:hover:bg-transparent'

export function getDropdownTriggerClass(
  theme: DropdownTheme = 'default',
  context: SalesDropdownTriggerContext = 'compact'
): string {
  if (theme === 'sales') {
    if (context === 'embedded') {
      return 'sd-dropdown-trigger sd-dropdown-trigger--embedded min-h-0 w-full border-0 bg-transparent shadow-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60'
    }
    return 'sd-dropdown-trigger sd-dropdown-trigger--compact w-full text-left transition-colors focus:outline-none focus:ring-1 focus:ring-[color:var(--sd-accent)] disabled:cursor-not-allowed disabled:opacity-60'
  }
  return dropdownDefaultTriggerClass
}

export function getDropdownTriggerValueClass(
  hasAppliedSelection: boolean,
  theme: DropdownTheme = 'default'
): string {
  if (theme === 'sales') {
    return hasAppliedSelection
      ? 'sd-dropdown-trigger-value--active font-medium'
      : 'text-text-secondary font-medium'
  }
  return hasAppliedSelection ? 'text-primary font-medium' : 'text-text-muted'
}

export function getDropdownChevronClass(
  theme: DropdownTheme = 'default',
  context: SalesDropdownTriggerContext = 'compact'
): string {
  if (theme === 'sales') {
    const sizeClass =
      context === 'embedded'
        ? 'sd-dropdown-chevron sd-dropdown-chevron--embedded ml-auto shrink-0 text-[10px] leading-none transition-transform'
        : 'sd-dropdown-chevron ml-auto shrink-0 text-base transition-transform'
    return sizeClass
  }
  return 'ri-arrow-down-s-line ml-auto shrink-0 text-lg text-text-muted transition-transform'
}

export function getDropdownMenuPanelClass(
  theme: DropdownTheme = 'default',
  mount: SalesDropdownMenuMount = 'teleport'
): string {
  if (theme === 'sales') {
    const surface =
      'sd-pages sd-dropdown-menu flex flex-col overflow-hidden rounded-md border shadow-xl'
    if (mount === 'inline') {
      return `${surface} absolute left-0 right-0 top-full z-50 mt-1.5`
    }
    return `${surface} fixed z-[10020]`
  }
  if (theme === 'dd') {
    const surface =
      'dd-pages dd-dropdown-menu flex flex-col overflow-hidden rounded-md border shadow-xl'
    if (mount === 'inline') {
      return `${surface} absolute left-0 right-0 top-full z-50 mt-1.5`
    }
    return `${surface} fixed z-[10020]`
  }
  if (mount === 'inline') {
    return 'absolute left-0 right-0 top-full z-50 mt-1.5 flex flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-xl'
  }
  return 'fixed z-[10020] flex flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-xl'
}

export function getDropdownSearchInputClass(theme: DropdownTheme = 'default'): string {
  if (theme === 'sales') {
    return 'sd-dropdown-search w-full rounded border bg-surface px-2 py-1 text-xs text-text outline-none'
  }
  return 'w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-text outline-none focus:border-primary'
}

export function getDropdownApplyButtonClass(theme: DropdownTheme = 'default'): string {
  if (theme === 'sales') {
    return 'sd-dropdown-apply rounded px-2.5 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90'
  }
  return 'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90'
}

export function getDropdownCancelButtonClass(theme: DropdownTheme = 'default'): string {
  if (theme === 'sales') {
    return 'rounded px-2.5 py-1 text-xs text-text-muted transition-colors hover:bg-surface-hover hover:text-text'
  }
  return 'rounded-md px-3 py-1.5 text-sm text-text-muted transition-colors hover:bg-surface-hover hover:text-text'
}

/** Compact Select all / Clear all action row for Sales flat multi-select menus. */
export function getSalesSelectAllRowClass(): string {
  return 'flex items-center justify-between gap-2 border-b border-[color:var(--sd-line)] px-2 py-1.5'
}

export function getSalesClearAllButtonClass(): string {
  return 'shrink-0 text-xs text-text-muted transition-colors hover:text-text disabled:cursor-not-allowed disabled:opacity-50'
}

/** Non-interactive optgroup-style section header inside dropdown menus. */
export function getDropdownGroupHeaderClass(theme: DropdownTheme = 'default'): string {
  if (theme === 'sales') {
    return 'px-2 pt-2 pb-0.5 text-[11px] font-medium tracking-[0.03em] text-[color:var(--sd-text-muted)] select-none pointer-events-none'
  }
  return 'px-6 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-text-muted select-none pointer-events-none'
}
